/**
 * @page /portal/claims
 * @description Claims-Listenansicht im Kundenportal.
 *   Zeigt eingereichte Schadensmeldungen mit Status-Tracking, Detail-Ansicht und Summary KPIs.
 * @api GET /api/claims-portal/my-claims, GET /api/claims-portal/claim/:id
 * @roles kunde
 * @i18n portal.claims
 * @since v2.52.1
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { PageHeader } from '../../components/ui';
import { Plus, ShieldAlert, ChevronRight, X, FileText, Image } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';
const api = (path) => fetch(`${API}${path}`, { credentials: 'include' }).then(r => r.json());

const STATUS_CFG = {
  submitted:             { color: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',   key: 'portal.claims.submitted' },
  in_review:             { color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',       key: 'portal.claims.processing' },
  documents_missing:     { color: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',   key: 'portal.claims.docsMissing' },
  approved:              { color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',       key: 'portal.claims.processing' },
  at_carrier:            { color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',       key: 'portal.claims.processing' },
  waiting_for_carrier:   { color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',       key: 'portal.claims.processing' },
  carrier_inquiry:       { color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',       key: 'portal.claims.processing' },
  carrier_response:      { color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',       key: 'portal.claims.processing' },
  objection:             { color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',       key: 'portal.claims.processing' },
  resolved:              { color: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400', key: 'portal.claims.done' },
  rejected:              { color: 'bg-red-500/15 text-red-600 dark:text-red-400',          key: 'portal.claims.rejected' },
};

const RESOLUTION_I18N = {
  refund: 'portal.claims.resRefund',
  partial_refund: 'portal.claims.resPartial',
  replaced: 'portal.claims.resReplaced',
  rejected: 'portal.claims.rejected',
};

function StatusBadge({ status, t }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.submitted;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cfg.color}`}>{t(cfg.key)}</span>;
}

function TypeLabel({ type, t }) {
  return type === 'damage' ? t('portal.claims.damaged') : t('portal.claims.lost');
}

function fmtDate(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ── Detail Modal ────────────────────────────────────────────────────────
function ClaimDetailModal({ claimId, onClose, t }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!claimId) return;
    setLoading(true);
    api(`/api/claims-portal/claim/${claimId}`)
      .then(d => setDetail(d))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [claimId]);

  if (!claimId) return null;

  const TIMELINE_LABELS = {
    created: 'Eingereicht',
    status_change: 'Status geändert',
    assigned: 'Zugewiesen',
    note: 'Notiz',
    document_uploaded: 'Dokument hochgeladen',
    resolved: 'Erledigt',
    email_sent: 'E-Mail gesendet',
    notification_sent: 'Benachrichtigung',
    ai_classified: 'AI-Klassifizierung',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-[var(--bg-elevated)] border border-[var(--border-card)] rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl mx-4" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-line">
          <div className="text-sm font-semibold text-content">
            {detail?.claim_number || `Claim #${claimId}`}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
          </div>
        ) : !detail ? (
          <div className="py-12 text-center text-sm text-[var(--text-secondary)]">Nicht gefunden</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-3">
              {[
                [t('portal.claims.orderNumber'), detail.order_number],
                [t('portal.claims.carrier'), detail.carrier_name],
                [t('portal.claims.trackingNumber'), detail.tracking_number],
                [t('portal.claims.type'), detail.claim_type === 'damage' ? t('portal.claims.damaged') : t('portal.claims.lost')],
                [t('portal.claims.status'), null, <StatusBadge key="sb" status={detail.status} t={t} />],
                [t('portal.claims.date'), fmtDate(detail.created_at)],
              ].map(([label, val, jsx], i) => (
                <div key={i}>
                  <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider font-semibold mb-0.5">{label}</div>
                  {jsx || <div className="text-sm text-content">{val || '–'}</div>}
                </div>
              ))}
            </div>

            {/* Description */}
            {detail.description && (
              <div>
                <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider font-semibold mb-1">{t('portal.claims.description')}</div>
                <div className="text-xs text-[var(--text-secondary)] leading-relaxed bg-surface-inset rounded-lg p-3">{detail.description}</div>
              </div>
            )}

            {/* Resolution */}
            {detail.resolution_type && (
              <div className="bg-surface-inset rounded-lg p-4">
                <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider font-semibold mb-2">{t('portal.claims.result')}</div>
                <div className="flex items-center gap-2 mb-1">
                  <StatusBadge status={detail.status} t={t} />
                  <span className="text-sm font-medium text-content">{t(RESOLUTION_I18N[detail.resolution_type] || detail.resolution_type)}</span>
                </div>
                {detail.resolution_amount > 0 && (
                  <div className="text-xs text-[var(--text-secondary)]">{t('portal.claims.amount')}: {detail.resolution_amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div>
                )}
                {detail.resolution_notes && (
                  <div className="text-xs text-[var(--text-secondary)] mt-1">{detail.resolution_notes}</div>
                )}
              </div>
            )}

            {/* Documents */}
            {detail.documents?.length > 0 && (
              <div>
                <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider font-semibold mb-2">{t('portal.claims.documents')}</div>
                <div className="flex flex-wrap gap-2">
                  {detail.documents.map(doc => (
                    <div key={doc.id} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-surface-inset text-xs text-[var(--text-secondary)]">
                      {doc.mime_type?.startsWith('image/') ? <Image className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                      <span className="truncate max-w-[140px]">{doc.file_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Timeline */}
            {detail.timeline?.length > 0 && (
              <div>
                <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider font-semibold mb-2">{t('portal.claims.timeline')}</div>
                <div className="space-y-2">
                  {detail.timeline.filter(e => !['ai_classified', 'assigned'].includes(e.event_type)).map(entry => (
                    <div key={entry.id} className="flex gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#338edc] mt-1.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs text-content">{entry.description || TIMELINE_LABELS[entry.event_type] || entry.event_type}</div>
                        <div className="text-[10px] text-[var(--text-tertiary)]">{fmtDate(entry.created_at)} {new Date(entry.created_at).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────
export default function PortalClaims() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    api('/api/claims-portal/my-claims')
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  const claims = data?.claims || [];
  const summary = data?.summary || { total: 0, in_progress: 0, resolved: 0 };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <PageHeader title={t('portal.claims.title')} subtitle={t('portal.claims.subtitle')} />
        <button onClick={() => navigate('/portal/claims/new')}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors whitespace-nowrap">
          <Plus className="w-3.5 h-3.5" /> {t('portal.claims.new')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { label: t('portal.claims.total'), value: summary.total },
          { label: t('portal.claims.inProgress'), value: summary.in_progress + summary.submitted },
          { label: t('portal.claims.resolved'), value: summary.resolved },
        ].map((card, i) => (
          <div key={i} className="bg-surface-card border border-line rounded-card p-4">
            <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-1">{card.label}</div>
            <div className="text-xl font-semibold font-mono text-content">{card.value}</div>
          </div>
        ))}
      </div>

      {/* Table or Empty State */}
      {claims.length === 0 ? (
        <div className="bg-surface-card border border-line rounded-card p-12 text-center">
          <ShieldAlert className="w-10 h-10 text-[var(--text-tertiary)] mx-auto mb-3" />
          <div className="text-sm font-semibold text-content mb-1">{t('portal.claims.empty')}</div>
          <div className="text-xs text-[var(--text-secondary)] mb-4">{t('portal.claims.emptyDesc')}</div>
          <button onClick={() => navigate('/portal/claims/new')}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
            <Plus className="w-3.5 h-3.5" /> {t('portal.claims.new')}
          </button>
        </div>
      ) : (
        <div className="bg-surface-card border border-line rounded-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-line">
                  {[
                    t('portal.claims.date'),
                    t('portal.claims.orderNumber'),
                    t('portal.claims.carrier'),
                    t('portal.claims.trackingNumber'),
                    t('portal.claims.type'),
                    t('portal.claims.status'),
                    '',
                  ].map((h, i) => (
                    <th key={i} className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {claims.map(claim => (
                  <tr key={claim.id} className="hover:bg-surface-hover transition-colors cursor-pointer" onClick={() => setDetailId(claim.id)}>
                    <td className="px-4 py-3 text-xs text-[var(--text-secondary)]">{fmtDate(claim.created_at)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-content">{claim.order_number}</td>
                    <td className="px-4 py-3 text-xs text-content">{claim.carrier_name || '–'}</td>
                    <td className="px-4 py-3 text-xs text-[var(--text-secondary)] font-mono">{claim.tracking_number}</td>
                    <td className="px-4 py-3 text-xs text-content"><TypeLabel type={claim.claim_type} t={t} /></td>
                    <td className="px-4 py-3"><StatusBadge status={claim.status} t={t} /></td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs text-[var(--text-secondary)] hover:text-[#338edc] transition-colors inline-flex items-center gap-0.5">
                        {t('portal.claims.details')} <ChevronRight className="w-3 h-3" />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <ClaimDetailModal claimId={detailId} onClose={() => setDetailId(null)} t={t} />
    </div>
  );
}
